/**
 * Scroll Carousel for Related Products
 * Auto-discovers carousels via [data-related-carousel-manual] attribute on container.
 *
 * Why external: inline <script> in Liquid section had a heisenbug where ScrollCarousel
 * was never instantiated when manual_recommendations=true, even though all DOM elements
 * existed correctly. Console logs paradoxically "fixed" it. Likely cause was HTML parser
 * error recovery from nested anchors / app inline-script interaction. Moving to external
 * .js bypasses inline script parsing entirely.
 */
class ScrollCarousel {
  constructor(container) {
    this.container = container;

    if (!this.container) {
      console.log('❌ Container not found');
      return;
    }

    if (this.container.dataset.initialized === 'true') {
      console.log('⚠️ Carousel already initialized');
      return;
    }
    this.container.dataset.initialized = 'true';

    // sectionId comes from container's id: "RelatedCarousel-<sectionId>"
    this.sectionId = (this.container.id || '').replace(/^RelatedCarousel-/, '');
    this.sectionRoot = document.querySelector('.related-products-section-' + CSS.escape(this.sectionId));

    this.gridGapDesktop = parseInt(this.container.dataset.gridGap || '16', 10);
    this.gridGapMobile = parseInt(this.container.dataset.gridGapMobile || '24', 10);

    this.track = this.container.querySelector('.carousel-track');
    this.prevBtn = this.sectionRoot ? this.sectionRoot.querySelector('.carousel-nav--prev') : null;
    this.nextBtn = this.sectionRoot ? this.sectionRoot.querySelector('.carousel-nav--next') : null;
    this.progressFill = this.sectionRoot ? this.sectionRoot.querySelector('.carousel-progress-fill') : null;

    if (!this.track) {
      console.warn('[carousel] no track found in', this.container.id);
      return;
    }

    this.cards = Array.from(this.track.querySelectorAll('.carousel-slide'));
    this.totalSlides = this.cards.length;
    this.isDesktop = window.innerWidth >= 750;
    this.cardsPerView = this.isDesktop ? 4 : 1;
    this.isScrolling = false;

    console.log('🎠 Scroll Carousel initialized:', {
      sectionId: this.sectionId,
      totalSlides: this.totalSlides,
      cardsPerView: this.cardsPerView,
    });

    this.init();
  }

  init() {
    if (this.totalSlides === 0) {
      console.warn('⚠️ No slides found');
      return;
    }

    this.bindCarouselEvents();
    this.setupGalleryNavigation();
    this.setupScrollListener();
    this.setupResponsive();
    this.updateNavButtons();

    setTimeout(() => {
      this.updateProgressBar();
    }, 100);
  }

  getGap() {
    return this.isDesktop ? this.gridGapDesktop : this.gridGapMobile;
  }

  bindCarouselEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollPrev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollNext();
      });
    }
  }

  scrollPrev() {
    const cardWidth = this.cards[0].offsetWidth;
    const scrollAmount = cardWidth + this.getGap();

    this.track.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollNext() {
    const cardWidth = this.cards[0].offsetWidth;
    const scrollAmount = cardWidth + this.getGap();

    this.track.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  }

  setupScrollListener() {
    let scrollTimeout;

    this.track.addEventListener(
      'scroll',
      () => {
        this.isScrolling = true;
        clearTimeout(scrollTimeout);

        this.updateProgressBar();

        scrollTimeout = setTimeout(() => {
          this.updateNavButtons();
          this.isScrolling = false;
        }, 150);
      },
      { passive: true }
    );
  }

  updateProgressBar() {
    if (!this.progressFill) return;

    const scrollLeft = this.track.scrollLeft;
    const cardWidth = this.cards[0].offsetWidth;
    const gap = this.getGap();

    const currentSnapIndex = Math.round(scrollLeft / (cardWidth + gap));
    const lastVisibleIndex = Math.min(currentSnapIndex + this.cardsPerView, this.totalSlides);
    const progressPercentage = (lastVisibleIndex / this.totalSlides) * 100;

    this.progressFill.style.width = progressPercentage + '%';
  }

  updateNavButtons() {
    if (!this.prevBtn || !this.nextBtn) return;

    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    this.prevBtn.disabled = scrollLeft <= 0;
    this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
  }

  setupGalleryNavigation() {
    this.container.querySelectorAll('.card-gallery').forEach((gallery) => {
      const prevBtn = gallery.querySelector('.gallery-nav--prev');
      const nextBtn = gallery.querySelector('.gallery-nav--next');
      const images = Array.from(gallery.querySelectorAll('.gallery-image'));

      if (images.length === 0) return;

      let currentImageIndex = 0;

      if (prevBtn && nextBtn) {
        const newPrevBtn = prevBtn.cloneNode(true);
        const newNextBtn = nextBtn.cloneNode(true);
        prevBtn.replaceWith(newPrevBtn);
        nextBtn.replaceWith(newNextBtn);

        ['mousedown', 'pointerdown'].forEach((evt) => {
          newPrevBtn.addEventListener(evt, (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
          newNextBtn.addEventListener(evt, (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
        });

        newPrevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.isScrolling) return;

          const prevIndex = currentImageIndex;
          currentImageIndex = currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
          this.swapGalleryImage(images, prevIndex, currentImageIndex, 'prev');
        });

        newNextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.isScrolling) return;

          const prevIndex = currentImageIndex;
          currentImageIndex = (currentImageIndex + 1) % images.length;
          this.swapGalleryImage(images, prevIndex, currentImageIndex, 'next');
        });
      }

      const galleryImages = gallery.querySelector('.gallery-images');
      if (galleryImages && images.length > 1) {
        let touchStartX = 0;
        let touchStartY = 0;

        galleryImages.addEventListener(
          'touchstart',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            touchStartX = e.changedTouches[0].screenX;
            touchStartY = e.changedTouches[0].screenY;
          },
          { passive: false }
        );

        galleryImages.addEventListener(
          'touchmove',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          { passive: false }
        );

        galleryImages.addEventListener('touchend', (e) => {
          e.stopPropagation();
          if (this.isScrolling) return;

          const touchEndX = e.changedTouches[0].screenX;
          const touchEndY = e.changedTouches[0].screenY;
          const diffX = touchStartX - touchEndX;
          const diffY = Math.abs(touchStartY - touchEndY);

          if (Math.abs(diffX) > 50 && diffY < 30) {
            const prevIndex = currentImageIndex;
            const direction = diffX > 0 ? 'next' : 'prev';

            if (diffX > 0) {
              currentImageIndex = (currentImageIndex + 1) % images.length;
            } else {
              currentImageIndex = currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
            }

            this.swapGalleryImage(images, prevIndex, currentImageIndex, direction);
          }
        });
      }
    });
  }

  swapGalleryImage(images, prevIndex, currentIndex, direction) {
    images.forEach((img, idx) => {
      if (idx !== prevIndex && idx !== currentIndex) {
        img.style.transition = 'none';
        img.style.transform = 'translateX(100%)';
        img.classList.remove('active', 'prev');
      }
    });

    const exitOffset = direction === 'next' ? '-100%' : '100%';
    const enterOffset = direction === 'next' ? '100%' : '-100%';

    images[prevIndex].style.transition = '';
    images[prevIndex].classList.remove('active');
    images[prevIndex].style.transform = 'translateX(' + exitOffset + ')';

    images[currentIndex].style.transition = 'none';
    images[currentIndex].style.transform = 'translateX(' + enterOffset + ')';
    images[currentIndex].classList.remove('prev');

    void images[currentIndex].offsetHeight;

    images[currentIndex].style.transition = '';
    images[currentIndex].style.transform = 'translateX(0)';
    images[currentIndex].classList.add('active');
  }

  setupResponsive() {
    let resizeTimeout;
    let lastWidth = window.innerWidth;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newWidth = window.innerWidth;
        const widthDiff = Math.abs(newWidth - lastWidth);

        if (widthDiff > 50) {
          const newIsDesktop = window.innerWidth >= 750;

          if (newIsDesktop !== this.isDesktop) {
            this.isDesktop = newIsDesktop;
            this.cardsPerView = this.isDesktop ? 4 : 1;
          }

          this.updateProgressBar();
          this.updateNavButtons();

          lastWidth = newWidth;
        }
      }, 250);
    });
  }
}

// Expose to window so the inline API path can also use it
window.ScrollCarousel = ScrollCarousel;

// Auto-init for manual mode (server-rendered cards via section schema default_related_products)
function initManualCarousels(scope) {
  const root = scope || document;
  root.querySelectorAll('[data-related-carousel-manual]').forEach((container) => {
    if (container.dataset.initialized === 'true') return;
    new ScrollCarousel(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initManualCarousels());
} else {
  initManualCarousels();
}

// Re-init when Shopify Theme Editor reloads section
document.addEventListener('shopify:section:load', (e) => initManualCarousels(e.target));
